import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { skuService } from '../../services/skus/skuService'
import { CreateSKUForm } from '../../types'
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler'

export const useSKUs = (params?: {
  page?: number
  limit?: number
  search?: string
  active_only?: boolean
}) => {
  return useQuery({
    queryKey: ['skus', params],
    queryFn: () => skuService.getSKUs(params),
  })
}

export const useSKU = (id: string) => {
  return useQuery({
    queryKey: ['sku', id],
    queryFn: () => skuService.getSKUById(id),
    enabled: !!id,
  })
}

export const useCreateSKU = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (skuData: CreateSKUForm) => skuService.createSKU(skuData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      showSuccessToast('SKU created successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useCreateSKUAtPosition = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (skuData: CreateSKUForm) => skuService.createSKUAtPosition(skuData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      showSuccessToast('SKU created and positioned successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateSKU = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSKUForm> }) =>
      skuService.updateSKU(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      queryClient.invalidateQueries({ queryKey: ['sku', id] })
      showSuccessToast('SKU updated successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateSKUAtPosition = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSKUForm> }) =>
      skuService.updateSKUAtPosition(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      queryClient.invalidateQueries({ queryKey: ['sku', id] })
      showSuccessToast('SKU updated and repositioned successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useDeleteSKU = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: string) => skuService.deleteSKU(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      showSuccessToast('SKU deleted successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateSKUStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      skuService.updateSKUStatus(id, isActive),
    onSuccess: (sku) => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      queryClient.invalidateQueries({ queryKey: ['sku', sku._id] })
      showSuccessToast(`SKU ${sku.is_active ? 'enabled' : 'disabled'} successfully!`)
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateSKUSortOrder = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (skuIds: string[]) => skuService.updateSKUSortOrder(skuIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skus'] })
      showSuccessToast('SKU order updated successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}
